import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { AlertCircle, Flag, MousePointerClick } from "lucide-react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { PageContainer } from "@/components/page-container";
import { PageHeader } from "@/components/page-header";
import { SignInStatusDot } from "@/components/signin-status-dot";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { ManagerReportRecentSignIn } from "@/types/reports";
import { trpc, withErrorToast } from "@/utils/trpc";

function formatTimestamp(value: string | null) {
  if (!value) return "Still signed in";
  return format(new Date(value), "PPpp");
}

function SignInsList() {
  const { pathname } = useLocation();
  const selectedId = pathname.startsWith("/signins/")
    ? pathname.split("/signins/")[1]
    : undefined;

  const {
    data: flaggedSignIns,
    isLoading,
    isError,
  } = useQuery({
    ...withErrorToast(trpc.getFlaggedSignIns.queryOptions()),
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>Flagged Sign-ins</CardTitle>
        <CardDescription>
          Review and manage flagged sign-in records that require attention.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isError ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertTitle>Failed to load flagged sign-ins</AlertTitle>
            <AlertDescription>
              Could not retrieve sign-in records. Please try again.
            </AlertDescription>
          </Alert>
        ) : (
          <FlaggedSignInsTable
            rows={flaggedSignIns ?? []}
            isLoading={isLoading}
            selectedId={selectedId}
            emptyLabel="No flagged sign-ins found."
          />
        )}
      </CardContent>
    </Card>
  );
}

export default function SignInsIndex() {
  const { pathname } = useLocation();
  const isDetail = pathname !== "/signins";

  return (
    <PageContainer className="space-y-6">
      <PageHeader
        title="Flagged sign-ins"
        description="Inspect sign-in records that need follow-up and open the full detail view."
      />

      {/* Mobile: show list OR detail */}
      <div className="lg:hidden">{isDetail ? <Outlet /> : <SignInsList />}</div>

      {/* Desktop: side-by-side */}
      <div className="hidden lg:grid lg:grid-cols-[1fr_380px] lg:gap-6 lg:items-start">
        <SignInsList />
        <div className="sticky top-24">
          {isDetail ? <Outlet /> : <DesktopDetailPlaceholder />}
        </div>
      </div>
    </PageContainer>
  );
}

function DesktopDetailPlaceholder() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-6 py-16 text-center text-muted-foreground">
      <MousePointerClick className="size-6 opacity-40" />
      <p className="text-sm">Select a sign-in to view details</p>
    </div>
  );
}

type FlaggedSignInsTableProps = {
  rows: ManagerReportRecentSignIn[];
  isLoading?: boolean;
  emptyLabel?: string;
  selectedId?: string;
};

function FlaggedSignInsTable({
  rows,
  isLoading,
  selectedId,
  emptyLabel = "No flagged sign-ins found.",
}: FlaggedSignInsTableProps) {
  if (isLoading) {
    return <p>Loading flagged sign-ins…</p>;
  }

  if (rows?.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>No flagged sign-ins</EmptyTitle>
          <EmptyDescription>{emptyLabel}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead></TableHead>
          <TableHead>Worker</TableHead>
          <TableHead>Job Title</TableHead>
          <TableHead>Site</TableHead>
          <TableHead>Signed in</TableHead>
          <TableHead>Signed out</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((entry) => (
          <TableRow
            key={entry.signInId}
            className={cn(selectedId === entry.signInId && "bg-muted/60")}
          >
            <TableCell>
              <SignInStatusDot online={!entry.signOutTime} />
            </TableCell>
            <TableCell className="capitalize">
              <Link to={`${entry.signInId}`} className="underline">
                {entry.workerName ?? "—"}
              </Link>
            </TableCell>
            <TableCell className="capitalize">
              {entry.jobTitle ?? "—"}
            </TableCell>
            <TableCell className="capitalize">{entry.siteName}</TableCell>
            <TableCell className="text-muted-foreground">
              {formatTimestamp(entry.signInTime)}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {entry.signOutTime
                ? formatTimestamp(entry.signOutTime)
                : "Still signed in"}
            </TableCell>
            <TableCell>
              <Badge variant="warning" className="gap-1">
                <Flag className="h-3 w-3" aria-hidden="true" />
                Flagged
              </Badge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
